import _ from 'lodash'
import { useCallback, useEffect, useRef } from 'react'
import { MeetingSessionConfiguration } from 'amazon-chime-sdk-js'
import {
  darkTheme,
  DeviceLabels,
  MeetingStatus,
  UserActivityProvider,
  useMeetingEvent,
  useMeetingManager,
  useMeetingStatus,
  MeetingManagerJoinOptions,
  useAudioInputs,
  useVideoInputs,
  useLocalVideo,
} from 'amazon-chime-sdk-component-library-react'
import { useNonDefaultDeviceChime } from '@/hooks/useNonDefaultDeviceChime'
import { ThemeProvider as ScThemeProvider } from 'styled-components'
import Box from '@mui/material/Box'
import ButtonGroup from '@mui/material/ButtonGroup'

import NoRemoteVideo from '@/components/ui/meeting/NoRemoteVideo'
import AttendeeControl from '@/components/chime-sdk/controls/AttendeeControl'
import AudioInputControl from '@/components/chime-sdk/controls/AudioInputControl'
import AudioOutputControl from '@/components/chime-sdk/controls/AudioOutputControl'
import ControlBar from '@/components/chime-sdk/controls/ControlBar'
import DeviceSelectionControl from '@/components/chime-sdk/controls/DeviceSelectionControl'
import ScreenShareControl from '@/components/chime-sdk/controls/ScreenShareControl'
import ScreenShotControl from '@/components/chime-sdk/controls/ScreenShotControl'
import VideoInputControl from '@/components/chime-sdk/controls/VideoInputControl'
import VideoTileGrid from '@/components/chime-sdk/grid/VideoTileGrid'
import { DeviceProps } from '@/components/ui/dialogs/DeviceSelectionDialog'
import OpenInNewTabControl from '@/components/chime-sdk/controls/OpenInNewTabControl'
import { BackgroundControl } from '@/features/chime/background'
import RecordingControl from '@/components/chime-sdk/controls/RecordingControl'
import { useGetConsultationQuery } from '@/core/services/consultation'
import { skipToken } from '@reduxjs/toolkit/query'
import useIsPds from '@/hooks/useIsPds'
import { useReportLicenseEnabled, useReportLicenseEnabledForUser } from '@/features/report'

export const SCREENSHOT_PREFIX = 'visioconference'

export type ChimeMeetingProps = {
  customDevices: DeviceProps[]
  disableScreenshot?: boolean
  localAttendeeInfo: any
  meetingInfo: any
  onReconnection: () => void
  onScreenshot?: (imageFile: File) => void
  screenshotCount?: number
  disableOpenInNewTab?: boolean
  consultationId?: string
}

export function ChimeMeeting(props: ChimeMeetingProps) {
  const {
    customDevices,
    disableScreenshot = false,
    localAttendeeInfo,
    meetingInfo,
    onReconnection,
    onScreenshot,
    screenshotCount = 0,
    disableOpenInNewTab,
    consultationId,
  } = props
  const meetingEvent = useMeetingEvent()
  const meetingManager = useMeetingManager()
  const meetingStatus = useMeetingStatus()
  const { devices: chimeAudioInputDevices, selectedDevice: selectedAudioInputDevice } = useAudioInputs()
  const { devices: chimeVideoInputDevices, selectedDevice: selectedVideoInputDevice } = useVideoInputs()
  const { isVideoEnabled } = useLocalVideo()
  const hasReportLicense = useReportLicenseEnabled()
  const isPds = useIsPds()
  const consultation = useGetConsultationQuery(consultationId ?? skipToken)
  const doctorRecordingPermission = useReportLicenseEnabledForUser(consultation.data?.medecin?._id ?? '')
  const disableRecording = doctorRecordingPermission && isPds

  useNonDefaultDeviceChime()

  const isCustomDevicesSelectedFirstTime = useRef<{ audio: boolean; video: boolean }>({ audio: false, video: false })

  const meetingId = meetingManager.meetingSession?.configuration.meetingId

  const startMeeting = useCallback(
    async (meetingInfo: any, attendeeInfo: any) => {
      const meetingSessionConfiguration = new MeetingSessionConfiguration(meetingInfo, attendeeInfo)
      const options: MeetingManagerJoinOptions = {
        deviceLabels: DeviceLabels.AudioAndVideo,
        enableWebAudio: true,
      }
      await meetingManager.join(meetingSessionConfiguration, options)
      await meetingManager.start()
    },
    [meetingManager]
  )

  useEffect(() => {
    if (meetingInfo && localAttendeeInfo) {
      startMeeting(meetingInfo, localAttendeeInfo)
    }
  }, [startMeeting, localAttendeeInfo, meetingInfo])

  // reconnect to a new meeting which is ended if a single attendee has been connecting but with no audio state changes for 30 minutes
  useEffect(() => {
    if (meetingEvent?.name === 'meetingEnded' && meetingStatus !== MeetingStatus.Left && meetingStatus !== MeetingStatus.JoinedFromAnotherDevice) {
      onReconnection()
    }
  }, [meetingEvent, meetingStatus, onReconnection])

  useEffect(() => {
    if (customDevices.length > 0 && meetingStatus === MeetingStatus.Succeeded && chimeAudioInputDevices.length && chimeVideoInputDevices.length) {
      const audioInputCustomDevices = _.filter(customDevices, ['type', 'audioinput'])
      const videoInputCustomDevices = _.filter(customDevices, ['type', 'videoinput'])

      if (audioInputCustomDevices[0] && audioInputCustomDevices[0].id !== selectedAudioInputDevice && !isCustomDevicesSelectedFirstTime.current.audio) {
        meetingManager.startAudioInputDevice(audioInputCustomDevices[0].id).then(() => {
          isCustomDevicesSelectedFirstTime.current.audio = true
        })
      }

      if (videoInputCustomDevices[0] && videoInputCustomDevices[0].id !== selectedVideoInputDevice && !isCustomDevicesSelectedFirstTime.current.video) {
        meetingManager.startVideoInputDevice(videoInputCustomDevices[0].id).then(() => {
          isCustomDevicesSelectedFirstTime.current.video = true
        })
      }
    }
  }, [chimeAudioInputDevices, chimeVideoInputDevices, customDevices, meetingManager, meetingStatus, selectedAudioInputDevice, selectedVideoInputDevice])

  useEffect(() => {
    return () => {
      meetingManager.leave()
    }
  }, [meetingManager])

  const handleCaptureScreenshot = (blob: Blob) => {
    const imgName = `${SCREENSHOT_PREFIX}-screenshot-${Date.now().toString()}.png`
    const file = new File([blob], imgName, { type: 'image/png' })
    onScreenshot?.(file)
  }

  return (
    <ScThemeProvider theme={darkTheme}>
      <UserActivityProvider>
        <Box sx={{ height: '100%', position: 'relative' }}>
          <VideoTileGrid layout="featured" localAttendeeName="Me" noRemoteVideoView={<NoRemoteVideo />} />
          {meetingStatus === MeetingStatus.Succeeded && localAttendeeInfo && (
            <>
              <ControlBar position="absolute">
                <AudioInputControl attendeeId={localAttendeeInfo.AttendeeId} />
                <VideoInputControl />
                <AudioOutputControl />
                <DeviceSelectionControl customDevices={customDevices} />
                {isVideoEnabled && <BackgroundControl />}
                {!disableOpenInNewTab && <OpenInNewTabControl />}
                {consultationId && hasReportLicense && meetingId && !disableRecording && (
                  <RecordingControl meetingId={meetingId} consultationId={consultationId} />
                )}
              </ControlBar>
              <ControlBar anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }} position="absolute">
                <ButtonGroup disableElevation>
                  {!disableScreenshot && (
                    <ScreenShotControl
                      count={screenshotCount}
                      onCaptureScreenShot={handleCaptureScreenshot}
                      sx={{ borderTopRightRadius: 'unset', borderBottomRightRadius: 'unset' }}
                    />
                  )}
                  <ScreenShareControl
                    sx={[disableScreenshot ? { borderTopRightRadius: 'unset', borderBottomRightRadius: 'unset' } : { borderRadius: 'unset' }]}
                  />
                  <AttendeeControl sx={{ borderTopLeftRadius: 'unset', borderBottomLeftRadius: 'unset' }} />
                </ButtonGroup>
              </ControlBar>
            </>
          )}
        </Box>
      </UserActivityProvider>
    </ScThemeProvider>
  )
}

export default ChimeMeeting
